/**
 * Coupon pickups scattered around the map.
 *
 * Kate: "there's also coupons around the map... To collect coupons, you run through
 * them." So there is no pick-up button — walking over one collects it.
 *
 * They are COUPONS, not coins. She says "coupons" ten times across the recordings;
 * "coins" appears exactly once, in passing ("your coupons and coins"), and "money" is
 * used loosely as a synonym. An earlier pass drew them as gold rings, which read as
 * coins and quietly contradicted her own word for them. A coupon is a paper ticket, so
 * that is what these are.
 */
import * as THREE from 'three'
import { MAP_HALF_EXTENT, WATER } from '../world/map'
import { heightAt } from '../world/terrain'

/** How close counts as running through one. */
const PICKUP_RADIUS = 1.3
/** Coupons in a single pickup. */
const COUPON_VALUE = 10
const BOB_HEIGHT = 0.25

interface Pickup {
  x: number
  z: number
  taken: boolean
  index: number
}

export class CouponField {
  readonly mesh: THREE.InstancedMesh
  private readonly pickups: Pickup[] = []
  private readonly matrix = new THREE.Matrix4()
  private readonly position = new THREE.Vector3()
  private readonly quaternion = new THREE.Quaternion()
  private readonly scale = new THREE.Vector3(1, 1, 1)
  private spin = 0

  constructor(count = 60, waterRadius = 9, seed = 99) {
    let a = seed >>> 0
    const random = (): number => {
      a = (a + 0x6d2b79f5) >>> 0
      let t = Math.imul(a ^ (a >>> 15), 1 | a)
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }

    // A paper ticket: a thin rectangle, printed side and back, standing upright so it
    // is visible in grass. Slightly wider than tall, the way a voucher reads.
    const geometry = new THREE.BoxGeometry(0.62, 0.34, 0.012)
    const material = new THREE.MeshStandardMaterial({
      map: createCouponTexture(),
      roughness: 0.75,
      metalness: 0,
      side: THREE.DoubleSide,
      // A gentle glow so a paper ticket still catches the eye on a dark jungle floor.
      emissive: 0xffe9a8,
      emissiveIntensity: 0.22,
    })
    this.mesh = new THREE.InstancedMesh(geometry, material, count)
    this.mesh.castShadow = true
    this.mesh.frustumCulled = false

    const edge = MAP_HALF_EXTENT - 4
    for (let i = 0; i < count; i++) {
      let x = 0
      let z = 0
      // Keep them out of the water and off the exact spawn point.
      for (let attempt = 0; attempt < 30; attempt++) {
        x = (random() * 2 - 1) * edge
        z = (random() * 2 - 1) * edge
        const inWater = waterRadius > 0 && Math.hypot(x - WATER.x, z - WATER.z) < waterRadius
        if (!inWater && Math.hypot(x, z) > 4) break
      }
      this.pickups.push({ x, z, taken: false, index: i })
    }
    this.writeMatrices()
  }

  private writeMatrices(): void {
    for (const pickup of this.pickups) {
      this.position.set(
        pickup.x,
        pickup.taken ? -50
          : heightAt(pickup.x, pickup.z) + 0.75 + Math.sin(this.spin * 1.6 + pickup.index) * BOB_HEIGHT,
        pickup.z,
      )
      this.quaternion.setFromAxisAngle(UP, this.spin + pickup.index)
      this.matrix.compose(this.position, this.quaternion, this.scale)
      this.mesh.setMatrixAt(pickup.index, this.matrix)
    }
    this.mesh.instanceMatrix.needsUpdate = true
  }

  get remaining(): number {
    return this.pickups.filter((p) => !p.taken).length
  }

  /**
   * Spin, bob, and collect anything the player runs through.
   * Returns how many coupons were picked up this step.
   */
  update(dt: number, x: number, z: number): number {
    this.spin += dt
    let collected = 0
    for (const pickup of this.pickups) {
      if (pickup.taken) continue
      if (Math.hypot(pickup.x - x, pickup.z - z) <= PICKUP_RADIUS) {
        pickup.taken = true
        collected += COUPON_VALUE
      }
    }
    this.writeMatrices()
    return collected
  }
}

const UP = new THREE.Vector3(0, 1, 0)

/**
 * The face of a coupon, drawn on a canvas: cream stock, a dashed tear-off line, a
 * notch at each end, and the value. No image files, same as every other texture here.
 */
function createCouponTexture(): THREE.CanvasTexture {
  const w = 256
  const h = 140
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')

  ctx.fillStyle = '#f6e3a8'
  ctx.fillRect(0, 0, w, h)

  // Border.
  ctx.strokeStyle = '#a8792c'
  ctx.lineWidth = 6
  ctx.strokeRect(7, 7, w - 14, h - 14)

  // Tear-off perforation, a third of the way along.
  ctx.setLineDash([9, 9])
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.moveTo(w * 0.34, 12)
  ctx.lineTo(w * 0.34, h - 12)
  ctx.stroke()
  ctx.setLineDash([])

  // The notches a punched ticket has.
  ctx.fillStyle = '#3f5c2a'
  for (const y of [0, h]) {
    ctx.beginPath()
    ctx.arc(w * 0.34, y, 11, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.fillStyle = '#8a5f1c'
  ctx.font = 'bold 62px system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('10', w * 0.17, h * 0.5)

  ctx.font = 'bold 30px system-ui, sans-serif'
  ctx.fillText('COUPON', w * 0.67, h * 0.5)

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 8
  return texture
}
